import { HubConnectionBuilder } from '@microsoft/signalr';
import * as Sentry from '@sentry/react';
import { create } from 'zustand/react';
import { ApiEndpoints } from '../../../constants/endpoints/apiEndpoints';
import { LobbyType } from '../../../features/workflow/types';
import { useAgentInboxItemStore } from '../stores/useAgentInboxItemStore';
import { AgentInboxModel } from '../table/types/types';
import { batchUpdatesRef, EventType } from '../utils/worker/UpdateBatcher';
import { InvokeRpc } from '../../../util/hubs/types';
import { useEffect } from 'react';
import { useFolioItemStore, FolioTab } from '../../folio/stores/useFolioItemStore';
// InboxCoordinator type - represents the role of the user updating priorities
type InboxCoordinator = string;

type AgentInboxRpcServer = {
  UpdatePriorityList: (subscription: LobbyType, idA: number, idB: number) => void;
  RemovePriority: (activityId: number) => void;
};
type InboxHubStore = {
  isConnected: boolean;
  isPriorityUpdating: boolean;
  isUpdating: boolean;
  /** When true, inbox hub updates for the Agent Inbox are ignored (per-user FE pause). */
  isEmailIntakePaused: boolean;
  setIsPriorityUpdating: (value: boolean) => void;
  setIsUpdating: (value: boolean) => void;
  setIsEmailIntakePaused: (value: boolean) => void;
  invoke: InvokeRpc<AgentInboxRpcServer> | null;
  setInvoke: (cb: InvokeRpc<AgentInboxRpcServer>) => void;
};

export const useInboxHubStore = create<InboxHubStore>((set) => ({
  isConnected: false,
  isPriorityUpdating: false,
  isUpdating: false,
  isEmailIntakePaused: false,
  invoke: null,
  setIsPriorityUpdating: (value: boolean) => set({ isPriorityUpdating: value }),
  setIsUpdating: (value: boolean) => set({ isUpdating: value }),
  setIsEmailIntakePaused: (value: boolean) => set({ isEmailIntakePaused: value }),
  setInvoke: (invoke) => set({ invoke, isConnected: true })
}));

export const InboxHub = () => {
  useEffect(() => {
    const rpc = {
      OnUpdate: (lobbyType: LobbyType, item: AgentInboxModel) => {
        const hubStore = useInboxHubStore.getState();
        // When email intake is paused, ignore inbox updates for this user only.
        if (lobbyType === LobbyType.Inbox && hubStore.isEmailIntakePaused) {
          return;
        }
        hubStore.setIsUpdating(true);
        const worker = batchUpdatesRef.getInstance();
        const eventType = lobbyType === LobbyType.Folio ? EventType.FolioUpdate : EventType.InboxUpdate;
        worker.postMessage({
          type: eventType,
          data: item
        });
      },
      OnRemove: (lobbyType: LobbyType, itemId: number) => {
        const hubStore = useInboxHubStore.getState();
        // When email intake is paused, ignore inbox removals for this user only.
        if (lobbyType === LobbyType.Inbox && hubStore.isEmailIntakePaused) {
          return;
        }
        hubStore.setIsUpdating(true);
        const worker = batchUpdatesRef.getInstance();
        const eventType = lobbyType === LobbyType.Folio ? EventType.FolioRemove : EventType.InboxRemove;
        worker.postMessage({
          type: eventType,
          data: { Id: itemId }
        });
      },
      OnPriorityListSwap: (
        _subscription: LobbyType,
        activityIdA: number,
        newIndexA: number,
        activityIdB: number,
        newIndexB: number,
        _updatedBy: InboxCoordinator
      ) => {
        useInboxHubStore.getState().setIsPriorityUpdating(true);
        useAgentInboxItemStore.getState().swapPriority(activityIdA, newIndexA, activityIdB, newIndexB);
      },
      OnManagerPriorityRemoved: (activityId: number, _oldManagerIndex: number) => {
        useInboxHubStore.getState().setIsPriorityUpdating(true);
        useAgentInboxItemStore.getState().removeFromPriority(activityId);
      }
    };
    const connection = new HubConnectionBuilder()
      .withUrl(ApiEndpoints.hubs.agentInbox)
      .withAutomaticReconnect([0, 2000, 10000, 30000, 60000, 120000])
      .build();

    const functions = Object.keys(rpc);
    for (const procedure of functions) {
      connection.on(procedure, (...args: any[]) => {
        (rpc[procedure as keyof typeof rpc] as any)(...args);
      });
    }

    connection
      .start()
      .then(async () => {
        const hubStore = useInboxHubStore.getState();

        // Set up invoke function
        hubStore.setInvoke((method, ...args) => {
          return connection.invoke(method, ...args);
        });

        await useAgentInboxItemStore.getState().init();
      })

      .catch((e) => {
        Sentry.captureException(e);
      });

    connection.onreconnecting(() => {
      useInboxHubStore.getState().setIsUpdating(true);
      useFolioItemStore.getState().setIsUpdating(true);
    });
    connection.onreconnected(async () => {
      await useAgentInboxItemStore.getState().init();

      // Conditionally refetch folio data if it was previously loaded
      const folioStore = useFolioItemStore.getState();
      if (folioStore.inboxInitialized) {
        await folioStore.init(FolioTab.Inbox);
      }
      if (folioStore.checkedOutInitialized) {
        await folioStore.init(FolioTab.CheckedOut);
      }
      if (folioStore.preRecInitialized) {
        await folioStore.init(FolioTab.PreRec);
      }
    });
    return () => {
      connection.stop().then();
    };
  }, []);
  return <></>;
};
